import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router';
import { capturarOrigem, iniciarPostHog } from '@titularis/analytics';
import { TitularisProvider } from '@titularis/ui';
import 'antd/dist/reset.css';
import { observarSaidasParaOApp } from './analytics';
import { App } from './App';
import { APP } from './destinos';
import './landing.css';

/*
 * O site passa a medir, e é a primeira vez.
 *
 * Até 13/09/2026 o `apps/app` tinha PostHog e o `apps/site` não tinha nada -
 * então não havia como saber se uma visita virou conta, nem de que canal ela
 * veio. Com a campanha do Enac prestes a começar, isso deixa de ser lacuna e
 * vira o próprio problema: dinheiro em anúncio sem denominador.
 *
 * `pageview: 'automatico'` porque a landing não tem política própria de tela: o
 * pacote de padrões do PostHog já acompanha troca de rota, e reimplementar isso
 * aqui só criaria uma segunda coisa para manter. O `apps/app` continua no modo
 * manual, e o porquê está lá.
 *
 * A ordem importa pouco, mas a `capturarOrigem` vem depois de propósito: ela
 * grava o **nosso** cookie de atribuição, que é o que viaja até o pedido no
 * banco e sobrevive a bloqueador de anúncio. O PostHog guarda a dele por
 * conta própria.
 */
iniciarPostHog({
  chave: import.meta.env.VITE_POSTHOG_KEY,
  host: import.meta.env.VITE_POSTHOG_HOST,
  pageview: 'automatico',
  /*
   * A landing grava; o app não. A assimetria está explicada no pacote, e o
   * resumo é: aqui não há produto na tela, e é aqui que chega o tráfego de
   * grupo e de Instagram - pelo navegador embutido do aplicativo, que é o
   * ambiente mais imprevisível e o mais difícil de depurar à distância. Sem
   * gravação, "duzentas pessoas entraram e saíram" é um número sem explicação.
   *
   * A Política de Privacidade descreve isto na cláusula de dados de navegação,
   * e as duas coisas mudam juntas ou nenhuma muda.
   */
  gravarSessao: true,
});

capturarOrigem();

/*
 * E, a partir de 17/09/2026, a landing também mede o que ela **faz**, e não só
 * quem chegou. Um ouvinte só, no documento, para as dezessete portas do funil:
 * instrumentar uma a uma deixaria a décima oitava nascer muda.
 */
observarSaidasParaOApp(APP);

/*
 * A landing usa o tema do produto - fontes, cor de marca, raio, tinta - sem
 * sobrescrever nada. Não é economia de código: é o que faz a tela de dentro
 * parecer o mesmo produto que a página de fora prometeu. Marca que muda de cara
 * no login perde metade da confiança que a página construiu.
 *
 * O `TitularisProvider` já carrega IBM Plex Sans e Serif desde o PR das
 * Configurações, então a landing não carrega fonte nenhuma por conta própria.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, networkMode: 'always' },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Elemento #root não encontrado.');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/*
        `padraoDeTema="light"`: a landing nasce clara. O desenho dela foi
        decidido em claro, e quem chega de um link no WhatsApp não escolheu ver
        a marca em modo escuro - o aparelho escolheu por ele. Quem preferir
        troca no seletor do cabeçalho, e a escolha fica guardada.
      */}
      <TitularisProvider padraoDeTema="light">
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </TitularisProvider>
    </QueryClientProvider>
  </StrictMode>,
);
